/**
 * normalizeVisaType — coerce a visa-type extraction value to PCB's canonical
 * dropdown form ("F-1", "J-1", "H-1B", etc.).
 *
 * Azure DI's pcb-visa-v2 model returns whatever the foil page reads (often
 * compact "F1"/"H1B" or "F 1"). The frontend dropdown in
 * IndividualNraSection lists hyphenated codes — without normalization the
 * extracted value never matches an <option> and the dropdown stays empty
 * even when extraction lit the AUTO badge.
 *
 * Returns the raw input (uppercased) when no mapping rule matches — never
 * returns empty for a non-empty input, so we never lose data.
 */

const KNOWN_VISA_TYPES: ReadonlyArray<string> = [
  'F-1', 'F-2', 'M-1', 'J-1', 'J-2',
  'H-1B', 'H-4', 'L-1', 'L-2', 'O-1',
  'TN', 'E-2', 'E-3',
  'B-1', 'B-2', 'VWP', 'ESTA',
];

export function normalizeVisaType(input: string | null | undefined): string {
  if (!input) return '';
  // Uppercase, strip spaces.
  const cleaned = input.toUpperCase().replace(/\s+/g, '');
  // Direct hit? (already hyphenated)
  if (KNOWN_VISA_TYPES.includes(cleaned)) return cleaned;
  // Try inserting a hyphen before the trailing digit-block.
  // "F1" → "F-1", "H1B" → "H-1B", "M1" → "M-1", "E2" → "E-2".
  const m = cleaned.match(/^([A-Z]+)(\d+[A-Z]?)$/);
  if (m) {
    const candidate = `${m[1]}-${m[2]}`;
    if (KNOWN_VISA_TYPES.includes(candidate)) return candidate;
  }
  // No-hyphen permanent passes: TN, VWP, ESTA.
  if (KNOWN_VISA_TYPES.includes(cleaned)) return cleaned;
  // Last resort — pass through uppercased input so officer can see + edit.
  return cleaned;
}
